import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Banknote, CheckCircle2, XCircle, Eye } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { Pagination } from "@/components/common/Pagination";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { DetailDialog, DetailRow } from "@/components/ui/detail-dialog";
import { UserName } from "@/components/common/EntityName";
import { withdrawalsApi } from "@/lib/api";
import type { Withdrawal } from "@/lib/api/types";
import { fmtCurrency, fmtRelativeTime } from "@/lib/utils";
import { usePagedQuery, PAGE_SIZE } from "@/lib/usePagedQuery";

const STATUS_VARIANT: Record<string, "success" | "warning" | "destructive" | "muted" | "sunset"> = {
  PENDING: "warning",
  APPROVED: "sunset",
  COMPLETED: "success",
  REJECTED: "destructive",
  CANCELLED: "muted",
};

export default function WithdrawalsList() {
  const qc = useQueryClient();
  const [detail, setDetail] = useState<Withdrawal | null>(null);
  const [confirm, setConfirm] = useState<{ kind: "APPROVED" | "COMPLETED" | "REJECTED"; w: Withdrawal } | null>(null);

  const {
    page, setPage, items, totalItems, totalPages, isFetching,
  } = usePagedQuery<Withdrawal>({
    key: ["withdrawals"],
    fetcher: withdrawalsApi.adminAll,
  });

  const process = useMutation({
    mutationFn: ({ id, status, adminNotes }: { id: string; status: string; adminNotes?: string }) =>
      withdrawalsApi.process(id, { status, adminNotes }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["withdrawals"] });
      setConfirm(null);
      setDetail(null);
    },
  });

  const destination = (w: Withdrawal) =>
    w.paymentMethod === "BANK" || w.bankName
      ? `${w.bankName ?? "Bank"} · ${w.accountNumber ?? ""}`
      : `${w.mobileProvider ?? "Mobile"} · ${w.accountNumber ?? ""}`;

  return (
    <>
      <PageHeader
        title="Withdrawals"
        subtitle={`${totalItems} payout requests from doctors. Approve, mark completed after sending money, or reject with a note.`}
      />

      <Card>
        <CardContent className="p-0">
          {items.length === 0 ? (
            <EmptyState
              icon={Banknote}
              title={isFetching ? "Loading…" : "No withdrawal requests"}
              description="Doctor payout requests appear here."
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Doctor</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Destination</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Requested</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((w) => (
                  <TableRow key={w.id} className="cursor-pointer" onClick={() => setDetail(w)}>
                    <TableCell className="text-sm font-semibold"><UserName id={w.userId} /></TableCell>
                    <TableCell className="text-sm font-bold">{fmtCurrency(w.amount, "TZS")}</TableCell>
                    <TableCell className="max-w-44 truncate text-xs text-muted-foreground">{destination(w)}</TableCell>
                    <TableCell><Badge variant={STATUS_VARIANT[w.status] ?? "muted"}>{w.status}</Badge></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{fmtRelativeTime(w.createdAt)}</TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1.5">
                        {w.status === "PENDING" && (
                          <>
                            <Button size="sm" onClick={() => setConfirm({ kind: "APPROVED", w })}>
                              <CheckCircle2 className="size-3.5" /> Approve
                            </Button>
                            <Button size="sm" variant="outline" className="border-destructive/40 text-destructive" onClick={() => setConfirm({ kind: "REJECTED", w })}>
                              <XCircle className="size-3.5" /> Reject
                            </Button>
                          </>
                        )}
                        {w.status === "APPROVED" && (
                          <Button size="sm" onClick={() => setConfirm({ kind: "COMPLETED", w })}>
                            <CheckCircle2 className="size-3.5" /> Mark paid
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => setDetail(w)}>
                          <Eye className="size-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <Pagination page={page} size={PAGE_SIZE} totalItems={totalItems} totalPages={totalPages} onChange={setPage} />
        </CardContent>
      </Card>

      <DetailDialog
        open={!!detail}
        onClose={() => setDetail(null)}
        icon={Banknote}
        title={detail ? fmtCurrency(detail.amount, "TZS") : ""}
        subtitle={detail ? `Withdrawal · ${detail.status}` : undefined}
      >
        {detail && (
          <>
            <DetailRow label="Doctor" value={<UserName id={detail.userId} />} />
            <DetailRow label="Method" value={detail.paymentMethod ?? "—"} />
            <DetailRow label="Destination" value={destination(detail)} />
            <DetailRow label="Account name" value={detail.accountName ?? "—"} />
            <DetailRow label="Balance at request" value={detail.availableBalance != null ? fmtCurrency(detail.availableBalance, "TZS") : "—"} />
            <DetailRow label="Doctor note" value={detail.notes ?? "—"} />
            <DetailRow label="Admin note" value={detail.adminNotes ?? "—"} />
            <DetailRow label="Transaction ref" value={detail.transactionReference ?? "—"} />
            <DetailRow label="Requested" value={fmtRelativeTime(detail.createdAt)} />
            <DetailRow label="Processed" value={detail.processedAt ? fmtRelativeTime(detail.processedAt) : "—"} />
          </>
        )}
      </DetailDialog>

      <ConfirmDialog
        open={confirm?.kind === "APPROVED"}
        onOpenChange={(v) => !v && setConfirm(null)}
        onConfirm={(note) => process.mutate({ id: confirm!.w.id, status: "APPROVED", adminNotes: note })}
        icon={CheckCircle2}
        tone="success"
        title="Approve this withdrawal?"
        description={`Approves ${fmtCurrency(confirm?.w.amount ?? 0, "TZS")} for payout. Mark it paid after sending the money.`}
        confirmLabel="Approve"
        loading={process.isPending}
        withReason
        reasonLabel="Note (optional)"
        reasonPlaceholder="e.g. Will be sent in today's batch"
      />
      <ConfirmDialog
        open={confirm?.kind === "COMPLETED"}
        onOpenChange={(v) => !v && setConfirm(null)}
        onConfirm={(note) => process.mutate({ id: confirm!.w.id, status: "COMPLETED", adminNotes: note })}
        icon={CheckCircle2}
        tone="success"
        title="Mark as paid?"
        description="Confirms the money has been sent to the doctor's account."
        confirmLabel="Mark paid"
        loading={process.isPending}
        withReason
        reasonLabel="Transaction reference (optional)"
        reasonPlaceholder="e.g. NMB transfer ref 12345"
      />
      <ConfirmDialog
        open={confirm?.kind === "REJECTED"}
        onOpenChange={(v) => !v && setConfirm(null)}
        onConfirm={(note) => process.mutate({ id: confirm!.w.id, status: "REJECTED", adminNotes: note })}
        icon={XCircle}
        tone="destructive"
        title="Reject this withdrawal?"
        description="The amount returns to the doctor's available balance. The note is visible to them."
        confirmLabel="Reject"
        loading={process.isPending}
        withReason
        reasonRequired
        reasonLabel="Reason"
        reasonPlaceholder="e.g. Account details don't match the registered name"
      />
    </>
  );
}
